<template>
  <v-app>
    <!-- Modern Top Bar -->
    <v-app-bar :color="APP_Color" elevation="0" height="64" class="super-admin-bar">
      <div class="admin-branding">
        <v-icon color="white" size="28" class="mr-3">mdi-shield-crown</v-icon>
        <div class="branding-text">
          <span class="admin-title">Super Admin</span>
          <span class="admin-subtitle">Panou de Administrare</span>
        </div>
      </div>

      <v-spacer />

      <v-chip
        :color="APP_Color"
        variant="outlined"
        size="small"
        class="version-chip"
      >
        <v-icon size="14" class="mr-1">mdi-shield-check</v-icon>
        Privilegii Complete
      </v-chip>

      <v-btn
        variant="text"
        @click="logout"
        class="logout-btn"
        prepend-icon="mdi-logout"
      >
        Deconectare
      </v-btn>
    </v-app-bar>

    <!-- Main Content -->
    <v-main class="admin-main">
      <v-container fluid class="admin-container">
        <!-- Modern Tabs -->
        <v-card class="tabs-card" elevation="4">
          <v-tabs
            v-model="selectedIndex"
            :color="APP_Color"
            class="modern-tabs"
            align-tabs="start"
          >
            <v-tab
              v-for="(tab, index) in tabs"
              :key="index"
              :value="index"
              class="tab-item"
            >
              <v-icon :icon="tab.icon" size="20" class="mr-2" />
              {{ tab.text }}
            </v-tab>
          </v-tabs>
        </v-card>

        <!-- Tab Content -->
        <v-card class="content-card" elevation="2">
          <v-window v-model="selectedIndex">
            <v-window-item :value="0">
              <div class="tab-content">
                <AdGridView />
              </div>
            </v-window-item>

            <v-window-item :value="1">
              <div class="tab-content">
                <AdSettings />
              </div>
            </v-window-item>

            <v-window-item :value="2">
              <div class="tab-content">
                <ERP_CompaniesGrid />
              </div>
            </v-window-item>
          </v-window>
        </v-card>
      </v-container>
    </v-main>

    <!-- Modern Footer -->
    <v-footer class="admin-footer" app>
      <div class="footer-content">
        <span class="footer-text">© 2025 Else Digital Solutions S.R.L.</span>
        <v-divider vertical class="mx-3" />
        <span class="footer-text">Super Admin Panel</span>
        <v-divider vertical class="mx-3" />
        <span class="footer-text">v{{ APP_Version }}</span>
      </div>
    </v-footer>
  </v-app>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import AppSettings from "@/settings/appSettings"
import AdGridView from "./AdGridView.vue"
import AdSettings from "./AdSettings.vue"
import ERP_CompaniesGrid from "./ERP_CompaniesGrid.vue"

export default defineComponent({
  name: "SuperAdmin",
  components: { AdGridView, AdSettings, ERP_CompaniesGrid },
  
  data() {
    return {
      tabs: [
        { text: "Utilizatori", icon: "mdi-account-group" },
        { text: "Configurare AD", icon: "mdi-cog" },
        { text: "Firme", icon: "mdi-domain" }
      ],
      selectedIndex: 0,
      APP_Color: AppSettings.APP_Color,
      APP_Version: AppSettings.APP_Version
    }
  },
  
  methods: {
    logout() {
      localStorage.removeItem("ERA_Jwt")
      localStorage.removeItem("ERA_Company")
      localStorage.removeItem("ERA_SuperAdminChoice")
      this.$router.go(0)
    }
  }
})
</script>

<style scoped>
/* ========== TOP BAR ========== */
.super-admin-bar {
  background: linear-gradient(135deg, #ff5722 0%, #f4511e 100%) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.admin-branding {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.branding-text {
  display: flex;
  flex-direction: column;
}

.admin-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}

.admin-subtitle {
  font-size: 0.75rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1;
}

.version-chip {
  color: white !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
  margin-right: 1rem;
}

.logout-btn {
  color: white !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.logout-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
}

/* ========== MAIN CONTENT ========== */
.admin-main {
  background: linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%);
  min-height: 100vh;
}

.admin-container {
  padding: 2rem !important;
  max-width: 1600px;
  margin: 0 auto;
}

/* ========== TABS CARD ========== */
.tabs-card {
  border-radius: 16px !important;
  margin-bottom: 1.5rem;
  overflow: hidden;
}

.modern-tabs {
  background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
}

.modern-tabs :deep(.v-tab) {
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 0.9375rem !important;
  letter-spacing: 0 !important;
  min-height: 56px !important;
  transition: all 0.2s;
}

.modern-tabs :deep(.v-tab:hover) {
  background: rgba(255, 87, 34, 0.05);
}

.modern-tabs :deep(.v-tab--selected) {
  font-weight: 600 !important;
}

.modern-tabs :deep(.v-tabs-slider) {
  height: 3px;
}

/* ========== CONTENT CARD ========== */
.content-card {
  border-radius: 16px !important;
  min-height: calc(100vh - 280px);
}

.tab-content {
  padding: 2rem;
}

/* ========== FOOTER ========== */
.admin-footer {
  background: white !important;
  border-top: 1px solid #e0e0e0;
  padding: 1rem 2rem !important;
}

.footer-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 0.5rem;
}

.footer-text {
  font-size: 0.875rem;
  color: #666;
}

/* ========== RESPONSIVE ========== */
@media (max-width: 960px) {
  .admin-container {
    padding: 1rem !important;
  }

  .tab-content {
    padding: 1rem;
  }

  .admin-subtitle {
    display: none;
  }

  .version-chip {
    display: none;
  }
}

@media (max-width: 600px) {
  .modern-tabs :deep(.v-tab) {
    min-width: auto !important;
    padding: 0 12px !important;
  }

  .tab-item :deep(.v-icon) {
    margin-right: 0 !important;
  }

  .tab-item :deep(span) {
    display: none;
  }
}
</style>
